import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { InitialData } from 'app/app.types';
import { AuthService } from 'app/core/auth/auth.service';
import { CamelCaseToWordsPipe } from 'app/shared/pipes/camel-case-to-words.pipe';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { webpages_Role } from 'app/shared/models/domain/data.model';

@Component({
    selector: 'dense-layout',
    templateUrl: './dense.component.html',
    encapsulation: ViewEncapsulation.None
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
    data: InitialData;
    isScreenSmall: boolean;
    isScreenMedium: boolean;
    panels: any[];
    drawerOpened: boolean = true;
    drawerMode: 'over' | 'side' = 'side';
    moduleTitle: string;
    moduleIcon: string;
    currentRole: string;
    navigationAppearance: 'default' | 'dense' = 'default';
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private camelPipe: CamelCaseToWordsPipe,
        private _httpClient: HttpClient
    ) {
    }
    get currentYear(): number {
        return new Date().getFullYear();
    }

    ngOnInit(): void {
        this.currentRole = 'Admin';
        const tabs = [];
        this._authService.localUserRoles
            .forEach((role: webpages_Role) => {
                Object.keys(environment.roleTabs)
                    .forEach((key) => {
                        if (environment.roleTabs[key].indexOf(role.roleName) > -1
                            && tabs.indexOf(key) == -1) {
                            tabs.push({ key: key, label: this.camelPipe.transform(key) });
                        }
                    });
            });
        const keys = tabs.map(t => t.key);
        // Subscribe to the resolved route data
        this._activatedRoute.data.subscribe((data: Data) => {
            this.data = data.initialData;
            this._httpClient.get<any>('api/common/navigation').subscribe((navigation) => {
                if (keys && keys.length) {
                    if (keys.indexOf("admin") > -1) {
                        this.data.navigation.horizontal = navigation.admin;
                        this.data.navigation.compact = navigation.adminCompact;
                    } else if (keys.indexOf("client") > -1) {
                        this.data.navigation.horizontal = navigation.client;
                        this.data.navigation.compact = navigation.client;
                    } else if (keys.indexOf("mmr") > -1) {
                        this.data.navigation.horizontal = navigation.mmr;
                        this.data.navigation.compact = navigation.mmr;
                    } else if (keys.indexOf("customer") > -1) {
                        this.data.navigation.horizontal = navigation.customer;
                        this.data.navigation.compact = navigation.customer;
                    } else if (keys.indexOf("auditor") > -1) {
                        this.data.navigation.horizontal = navigation.auditor;
                        this.data.navigation.compact = navigation.auditor;
                    } else if (keys.indexOf("salesRep") > -1) {
                        this.data.navigation.horizontal = navigation.salesRep;
                        this.data.navigation.compact = navigation.salesRepCompact;
                        this.currentRole = 'Sales Rep';
                    } else {
                        this.data.navigation.horizontal = navigation.default;
                        this.data.navigation.compact = navigation.compact;
                    }
                } else {
                    this.data.navigation.horizontal = navigation.default;
                    this.data.navigation.compact = navigation.compact;
                }
            });

            this._fuseMediaWatcherService.onMediaChange$
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(({ matchingAliases }) => {
                    // Check if the screen is small
                    this.isScreenSmall = !matchingAliases.includes('md');
                    this.isScreenMedium = !matchingAliases.includes('lg');
                    // Change the navigation appearance
                    // this.navigationAppearance = this.isScreenSmall ? 'default' : 'dense';
                });
            this._processRoute();
        });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
                this.isScreenMedium = !matchingAliases.includes('lg');
            });

        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this._processRoute();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    toggleNavigation(name: string): void {
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);
        if (navigation) {
            navigation.toggle();
        }
    }

    toggleNavigationAppearance(): void {
        this.navigationAppearance = (this.navigationAppearance === 'default' ? 'dense' : 'default');
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    private _processRoute(): void {
        this.panels = null;
        let route = this._activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        if (route?.snapshot?.data?.panels === false) {
            this.drawerOpened = false;
        }
        else if (route?.snapshot?.data?.panels) {
            this.panels = route.snapshot.data.panels;
            this.moduleTitle = route.snapshot.data.moduleTitle || 'Title';
            this.moduleIcon = route.snapshot.data.moduleIcon || 'mat_outline:admin_panel_setting';
            this.drawerOpened = true;
        } else if (route?.parent?.snapshot?.data?.panels === false) {
            this.drawerOpened = false;
        } else if (route?.parent?.snapshot?.data?.panels) {
            this.panels = route.parent.snapshot.data.panels;
            this.moduleTitle = route.parent.snapshot.data.moduleTitle || 'Title';
            this.moduleIcon = route.parent.snapshot.data.moduleIcon || 'mat_outline:admin_panel_setting';
            this.drawerOpened = true;
        } else {
            this.drawerOpened = false;
        }
    }
}
